/**
 * Normalizarea numelor de scoli — DOAR la afisare
 *
 * Numele vin din importul MEC si din ce tasteaza profesorii: majoritatea sunt
 * integral cu majuscule („SCOALA GIMNAZIALA NR. 12"), cu spatii duble sau cu
 * spatiu inaintea punctuatiei. Intr-un tabel de 500 de randuri, un bloc de
 * majuscule se citeste mult mai greu decat acelasi text in Title Case.
 *
 * 🔴 LIMITELE, asumate (decizie S0, 2026-09-03):
 * - **Nu se ating datele.** Functia asta e pur de afisare. Baza, exportul Excel
 *   si filtrele pe coloana lucreaza mai departe pe textul original — altfel
 *   utilizatorul ar cauta dupa ce vede si n-ar gasi nimic.
 * - **Ghilimelele rupte** (`"`, `''`, `,,`) si **localitatea lipita de nume**
 *   NU se repara aici. Sunt greseli de continut, se repara in date, nu la
 *   afisare.
 *
 * Regula de siguranta cea mai importanta: **daca numele are deja litere mici,
 * nu se atinge deloc** (doar spatiile). Cine l-a scris corect a scris si
 * majusculele corect; o retitrare oarba ar strica exact numele bune.
 */

/**
 * Cuvintele care raman cu litera mica in interiorul unui nume propriu romanesc.
 * Prima pozitie face exceptie — „De la Colegiu" nu exista, dar „Liceul de Arte" da.
 */
const CUVINTE_MICI = new Set([
    'a', 'al', 'ai', 'ale', 'cu', 'de', 'din', 'dintre', 'fara', 'fără',
    'in', 'în', 'intre', 'între', 'la', 'pe', 'pentru', 'si', 'și', 'sub', 'sau',
]);

/**
 * Romana are DOUA forme de diacritice in circulatie: cu virgula dedesubt
 * (`ș` U+0219, `ț` U+021B — forma corecta, standard din 1993) si cu sedila
 * (`ş` U+015F, `ţ` U+0163 — mostenire din Latin-2). Sunt caractere DIFERITE:
 * `'ŞI' !== 'ȘI'`, iar un `Set` scris cu una nu potriveste textul scris cu alta.
 *
 * Codul aplicatiei e uniform pe virgula (masurat: 1.120 de aparitii cu virgula,
 * zero cu sedila, in afara exemplului din docstring-ul de mai jos). Dar NUMELE DE
 * SCOLI vin din importul MEC, si nimeni nu garanteaza forma. Masurat inainte de
 * reparatia asta: `COLEGIUL DE ARTE ŞI MESERII` iesea `Colegiul de Arte Şi
 * Meserii` — cu „Şi" cu majuscula, pentru ca `CUVINTE_MICI` are `și` si `si`, dar
 * nu si `şi`.
 *
 * Se normalizeaza DOAR cheia de comparatie. Textul afisat pastreaza diacriticele
 * exact cum au venit, cum promite docstring-ul functiei.
 */
const SEDILA_LA_VIRGULA: Record<string, string> = {
    'ş': 'ș', 'Ş': 'Ș', 'ţ': 'ț', 'Ţ': 'Ț',
};
const cheieDiacritice = (text: string): string =>
    text.replace(/[şŞţŢ]/g, (c) => SEDILA_LA_VIRGULA[c]);

/** Cifre romane folosite in nume de scoli („Nr. IV", „Clasa a VIII-a"). */
const CIFRE_ROMANE = /^[IVXLCDM]+(-[A-Za-zĂÂÎȘȚăâîșț]+)?$/;

/** Un simbol care nu contine nicio litera (cifre, punctuatie, ghilimele). */
const FARA_LITERE = /^[^\p{L}]+$/u;

/** Acronim scurt scris cu puncte: „C.N.", „A.S.". */
const ACRONIM_CU_PUNCTE = /^(\p{Lu}\.){2,}$/u;

function titluCuvant(cuvant: string, esteLaInceput: boolean): string {
    if (!cuvant) return cuvant;
    if (FARA_LITERE.test(cuvant)) return cuvant;
    if (ACRONIM_CU_PUNCTE.test(cuvant)) return cuvant;

    // „NR." → „Nr.", dar „IV" ramane „IV" si „VIII-A" ramane asa.
    const miez = cuvant.replace(/[^\p{L}\-]/gu, '');
    if (CIFRE_ROMANE.test(cheieDiacritice(miez))) return cuvant;

    const mic = cuvant.toLocaleLowerCase('ro-RO');
    if (!esteLaInceput && CUVINTE_MICI.has(cheieDiacritice(mic.replace(/[^\p{L}]/gu, '')))) return mic;

    // Majuscula dupa fiecare separator din interiorul cuvantului: cratima,
    // apostrof, ghilimele deschise si virgula lipita („Sfantul-Gheorghe",
    // „O'Neill", „«Ion", „,,Ion" — ghilimeaua rupta din date).
    return mic.replace(
        /(^|[-'’‘„",.«(\/])(\p{L})/gu,
        (_, separator: string, litera: string) => separator + litera.toLocaleUpperCase('ro-RO')
    );
}

/**
 * Curata spatiile si, daca numele e integral cu majuscule, il trece in Title Case.
 *
 * @example
 * normalizeSchoolName('ŞCOALA  GIMNAZIALA NR. 12 „CODRII VERZI"')
 * // → 'Şcoala Gimnazială Nr. 12 „Codrii Verzi"'   (diacriticele raman cum sunt)
 * normalizeSchoolName('Liceul Teoretic "Mihai Eminescu"')
 * // → neschimbat: are deja litere mici
 */
export function normalizeSchoolName(nume: string | null | undefined): string {
    if (nume == null) return '';
    const curatat = String(nume).replace(/\s+/g, ' ').trim();
    if (!curatat) return '';

    // Are litere mici ⇒ a fost scris de om, nu de importul cu majuscule. Nu se atinge.
    if (/\p{Ll}/u.test(curatat)) return curatat;

    return curatat
        .split(' ')
        .map((cuvant, index) => titluCuvant(cuvant, index === 0))
        .join(' ');
}
